import axios from "axios";

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

/**
 * The installed request interceptor, so a second call replaces it rather than
 * stacking another one.
 *
 * This mattered: the interceptor used to be added from a `useEffect` in
 * InsightsLayout keyed on `getAccessTokenSilently`, with no cleanup, so every run
 * of that effect added another. Each one calls `getAccessTokenSilently` in turn, so
 * N interceptors meant N token acquisitions per request -- invisible, because
 * Auth0's client caches and they all set the same header.
 */
let requestInterceptorId = null;
let responseInterceptorId = null;

/**
 * Teaches the client to send the caller's Auth0 access token.
 *
 * Idempotent: calling it again replaces the previous interceptor. Returns a
 * function that removes it, so a caller mounting it from an effect can clean up.
 *
 * @param {() => Promise<string>} getAccessTokenSilently from `useAuth0()`
 * @param {{ onUnauthenticated?: () => void, onForbidden?: (error: unknown) => void }} [handlers]
 *   `onUnauthenticated` runs on a 401 -- the token is missing or expired, so the
 *   viewer has to sign in again. `onForbidden` runs on a 403 -- the request was
 *   understood and refused, which is a fact about this viewer's permissions and
 *   not something re-authenticating fixes.
 * @returns {() => void} removes both interceptors
 */
export const addAccessTokenInterceptor = (
  getAccessTokenSilently,
  handlers = {}
) => {
  if (requestInterceptorId !== null) {
    axiosClient.interceptors.request.eject(requestInterceptorId);
  }
  if (responseInterceptorId !== null) {
    axiosClient.interceptors.response.eject(responseInterceptorId);
  }

  requestInterceptorId = axiosClient.interceptors.request.use(async (config) => {
    const token = await getAccessTokenSilently();
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  // There was no response interceptor at all, so a 401 surfaced as whatever the
  // calling component did with a rejected promise -- usually an empty table. The
  // handlers stay optional and the error is ALWAYS re-thrown: this observes, it
  // does not swallow, because every call site already decides what its own
  // failure looks like.
  responseInterceptorId = axiosClient.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      if (status === 401) handlers.onUnauthenticated?.();
      if (status === 403) handlers.onForbidden?.(error);
      return Promise.reject(error);
    }
  );

  return () => {
    if (requestInterceptorId !== null) {
      axiosClient.interceptors.request.eject(requestInterceptorId);
      requestInterceptorId = null;
    }
    if (responseInterceptorId !== null) {
      axiosClient.interceptors.response.eject(responseInterceptorId);
      responseInterceptorId = null;
    }
  };
};

export default axiosClient;
